import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // Fileauvert — court-circuit : si la nouvelle galerie porte exactement les mêmes médias
        // dans le même ordre (cas nominal des formats 100g/200g/400g qui partagent leurs photos),
        // on ne remplace RIEN. Le swap DOM intégral faisait flasher les photos et reconstruisait
        // le rail de miniatures pour un rendu final identique. Signature = data-media-id par
        // slide, ordre compris (le serveur réordonne les médias par variante → un ordre différent
        // est un vrai changement et doit passer par le remplacement ci-dessous).
        const mediaSignature = (gallery) =>
          Array.from(gallery.querySelectorAll('slideshow-slide'))
            .map((slide) => slide.querySelector('[data-media-id]')?.getAttribute('data-media-id') ?? '')
            .join('|');
        if (mediaSignature(this) === mediaSignature(newMediaGallery)) return;

        // Fileauvert — conserver la photo affichée au changement de variante : le remplacement
        // intégral repartait sur la 1re slide (et le serveur réordonne les médias par variante,
        // donc on repère le média courant par data-media-id, pas par index).
        // ⚠️ NE PAS passer par l'attribut initial-slide : le chemin d'init du slideshow fait
        // `current = initialSlideIndex` PUIS select(même index) → early-return « déjà courant »
        // sans jamais scroller (prouvé sur :9292). On appelle donc select(index) APRÈS insertion,
        // sur l'élément upgradé, avec vérification d'effet + retry (le driver de scroll interne
        // s'initialise de façon asynchrone et select() no-op silencieusement avant ça).
        // Dans le modal quick-add (dialog), la galerie est une pile statique sans navigation :
        // on laisse le comportement natif, la restauration ne concerne que la fiche produit.
        const currentSlide = this.closest('dialog') ? null : this.slideshow?.slides?.[this.slideshow.current];
        const currentMediaId = currentSlide?.querySelector('[data-media-id]')?.getAttribute('data-media-id');
        let restoreIndex = -1;
        if (currentMediaId) {
          const newSlides = Array.from(newMediaGallery.querySelectorAll('slideshow-slide'));
          restoreIndex = newSlides.findIndex((slide) => slide.querySelector(`[data-media-id="${currentMediaId}"]`));
        }

        // Fileauvert — médias réellement différents (variante avec photos propres) : remplacement
        // en fondu doux plutôt qu'en coupe sèche. Hauteur de l'ancienne galerie réservée en
        // min-height le temps du fondu → zéro layout shift pendant que les nouvelles images
        // chargent. prefers-reduced-motion → swap sec (comportement d'origine).
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!reduceMotion) {
          const { height } = this.getBoundingClientRect();
          if (height > 0) newMediaGallery.style.minHeight = `${Math.round(height)}px`;
          newMediaGallery.style.opacity = '0';
        }

        this.replaceWith(newMediaGallery);

        if (!reduceMotion) {
          // Double rAF : laisser le navigateur peindre l'état opacity:0 avant de lancer la
          // transition (un seul rAF peut fusionner les deux styles dans la même frame).
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              newMediaGallery.style.transition = 'opacity 250ms cubic-bezier(.22, 1, .36, 1)';
              newMediaGallery.style.opacity = '1';
              setTimeout(() => {
                newMediaGallery.style.removeProperty('transition');
                newMediaGallery.style.removeProperty('opacity');
                newMediaGallery.style.removeProperty('min-height');
              }, 300);
            });
          });
        }

        if (restoreIndex > 0) {
          const restore = (attempt) => {
            const slideshow = /** @type {any} */ (newMediaGallery.querySelector('slideshow-component'));
            if (slideshow && typeof slideshow.select === 'function') {
              slideshow.select(restoreIndex, undefined, { animate: false });
            }
            const done =
              slideshow?.current === restoreIndex &&
              slideshow?.refs?.slides?.[restoreIndex]?.getAttribute('aria-hidden') === 'false';
            if (!done) {
              if (attempt < 20) {
                requestAnimationFrame(() => restore(attempt + 1));
              } else {
                console.warn('[media-gallery] photo affichée non restaurée après changement de variante');
              }
            }
          };
          requestAnimationFrame(() => restore(0));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
